import {
  advanceManagedRuntimeStateCursor,
  applyThreadSyncBaselineEventsToManagedRuntimeState,
  applyThreadSyncCatchupEventsToManagedRuntimeState,
} from "@/lib/thread-sync-inactive-replica-computation"
import {
  buildEmptyManagedRuntimeState,
  normalizeManagedRuntimeState,
} from "@/lib/thread-sync-replica-runtime"
import { readNewestThreadReplicaCheckpoint } from "@/lib/thread-replica-checkpoint-store"
import { readThreadReplicaLaneEventBatches } from "@/lib/thread-replica-event-store"
import {
  EMPTY_THREAD_REPLICA_CURSOR,
  FIRST_THREAD_REPLICA_SEQUENCE,
  type ThreadReplicaHydrateResult,
  type ThreadReplicaScope,
} from "@/lib/thread-replica-types"
import { readThreadReplicaMeta } from "@/lib/thread-replica-meta-store"

function isCheckpointCursorCompatible(args: {
  checkpointCursor: number
  checkpointLastEventId: number | null
  checkpointProviderEventId: number | null
  hasSnapshot: boolean
}): boolean {
  if (args.checkpointCursor === EMPTY_THREAD_REPLICA_CURSOR) {
    return (
      (args.checkpointLastEventId ?? EMPTY_THREAD_REPLICA_CURSOR) ===
      EMPTY_THREAD_REPLICA_CURSOR
    )
  }

  if (args.hasSnapshot === false) {
    return args.checkpointLastEventId === args.checkpointCursor
  }

  return (
    args.checkpointLastEventId === args.checkpointCursor &&
    args.checkpointProviderEventId === args.checkpointCursor
  )
}

function didThreadReplicaMetaChangeDuringHydration(args: {
  initialMeta: Awaited<ReturnType<typeof readThreadReplicaMeta>>
  latestMeta: Awaited<ReturnType<typeof readThreadReplicaMeta>>
}): boolean {
  if (!args.initialMeta || !args.latestMeta) {
    return args.initialMeta !== args.latestMeta
  }

  return (
    args.initialMeta.replicaEpoch !== args.latestMeta.replicaEpoch ||
    args.initialMeta.localCursor !== args.latestMeta.localCursor ||
    args.initialMeta.oldestAvailableCursor !== args.latestMeta.oldestAvailableCursor ||
    args.initialMeta.coverage !== args.latestMeta.coverage ||
    args.initialMeta.lastBaselineSequence !== args.latestMeta.lastBaselineSequence ||
    args.initialMeta.lastCheckpointCursor !== args.latestMeta.lastCheckpointCursor ||
    args.initialMeta.lastUpdatedAtMs !== args.latestMeta.lastUpdatedAtMs
  )
}

const DEFAULT_HYDRATE_CONVERSATION_ID = "default"

function normalizeHydrateConversationId(value: string | null | undefined): string {
  if (typeof value !== "string") return DEFAULT_HYDRATE_CONVERSATION_ID
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : DEFAULT_HYDRATE_CONVERSATION_ID
}

export async function hydrateThreadReplica(args: ThreadReplicaScope & {
  targetCursor?: number
  /**
   * Conversation whose projection should be rebuilt. The shared runtime lane
   * contains rows from every conversation in this session, so hydration needs
   * to know which subset to apply. Omitted/null is treated as the default
   * conversation for legacy callers; foreground hot paths should pass an
   * explicit id.
   */
  conversationId?: string | null
}): Promise<ThreadReplicaHydrateResult> {
  const activeConversationId = normalizeHydrateConversationId(args.conversationId)
  let initialMeta = await readThreadReplicaMeta(args)
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const meta = initialMeta
    if (!meta) {
      return { status: "missing", reason: "missing_meta" }
    }

    if (meta.localCursor <= EMPTY_THREAD_REPLICA_CURSOR && meta.lastBaselineSequence <= EMPTY_THREAD_REPLICA_CURSOR) {
      return { status: "missing", reason: "empty_replica" }
    }

    const targetCursor = Math.max(
      EMPTY_THREAD_REPLICA_CURSOR,
      Math.min(
        meta.localCursor,
        Number.isFinite(args.targetCursor) ? Math.floor(args.targetCursor ?? EMPTY_THREAD_REPLICA_CURSOR) : meta.localCursor,
      ),
    )
    const oldestAvailableRuntimeSequence =
      meta.oldestAvailableCursor >= FIRST_THREAD_REPLICA_SEQUENCE
        ? meta.oldestAvailableCursor
        : FIRST_THREAD_REPLICA_SEQUENCE
    const checkpointTargetCursor =
      meta.coverage === "tail_window" && targetCursor >= oldestAvailableRuntimeSequence
        ? oldestAvailableRuntimeSequence - 1
        : targetCursor

    const checkpoint = await readNewestThreadReplicaCheckpoint({
      scopeKey: args.scopeKey,
      sessionId: args.sessionId,
      replicaEpoch: meta.replicaEpoch,
      targetCursor: checkpointTargetCursor,
      eventContractVersion: meta.eventContractVersion,
      projectorVersion: meta.projectorVersion,
      // Ask only for checkpoints matching the target conversation. The reader
      // treats legacy null-tagged checkpoints as default-conversation, which
      // keeps single-chat sessions working without migration.
      conversationId: activeConversationId,
    })

    const normalizedCheckpoint = checkpoint
      ? {
          ...checkpoint,
          runtimeState: normalizeManagedRuntimeState({
            cursor: checkpoint.cursor,
            runtimeState: checkpoint.runtimeState,
          }),
        }
      : null
    const compatibleCheckpoint =
      normalizedCheckpoint &&
      isCheckpointCursorCompatible({
        checkpointCursor: normalizedCheckpoint.cursor,
        checkpointLastEventId: normalizedCheckpoint.runtimeState.lastEventId,
        checkpointProviderEventId: normalizedCheckpoint.runtimeState.snapshot?.cursorState.providerEventId ?? null,
        hasSnapshot: normalizedCheckpoint.runtimeState.snapshot !== null,
      })
        ? normalizedCheckpoint
        : null

    let runtimeState = normalizeManagedRuntimeState({
      runtimeState: compatibleCheckpoint?.runtimeState ?? buildEmptyManagedRuntimeState(),
    })
    const baselineStart = compatibleCheckpoint
      ? compatibleCheckpoint.lastBaselineSequence + 1
      : FIRST_THREAD_REPLICA_SEQUENCE
    const tailWindowCoverageRequiresGaplessTarget =
      meta.coverage === "tail_window" && oldestAvailableRuntimeSequence > FIRST_THREAD_REPLICA_SEQUENCE
    const tailWindowCheckpointCoversTarget =
      compatibleCheckpoint !== null &&
      (
        targetCursor >= oldestAvailableRuntimeSequence
          ? compatibleCheckpoint.cursor >= oldestAvailableRuntimeSequence - 1
          : compatibleCheckpoint.cursor >= targetCursor
      )
    if (
      tailWindowCoverageRequiresGaplessTarget &&
      !tailWindowCheckpointCoversTarget
    ) {
      const latestMeta = await readThreadReplicaMeta(args)
      if (attempt === 0 && didThreadReplicaMetaChangeDuringHydration({ initialMeta: meta, latestMeta })) {
        initialMeta = latestMeta
        continue
      }
      return {
        status: "reset_required",
        reason: "integrity_gap",
        meta,
        targetCursor,
      }
    }
    const runtimeStart = compatibleCheckpoint
      ? Math.max(compatibleCheckpoint.cursor + 1, oldestAvailableRuntimeSequence)
      : FIRST_THREAD_REPLICA_SEQUENCE

    const expectedBaselineReplayCount =
      meta.lastBaselineSequence >= baselineStart
        ? meta.lastBaselineSequence - baselineStart + 1
        : 0
    let baselineReplayCount = 0
    const baselineReadResult =
      expectedBaselineReplayCount > 0
        ? await readThreadReplicaLaneEventBatches({
            scopeKey: args.scopeKey,
            sessionId: args.sessionId,
            lane: "baseline",
            fromSequence: baselineStart,
            toSequence: meta.lastBaselineSequence,
            onBatch: (rows) => {
              baselineReplayCount += rows.length
              runtimeState = applyThreadSyncBaselineEventsToManagedRuntimeState({
                currentState: runtimeState,
                events: rows.map((row) => ({
                  baselineSeq: row.sequence,
                  type: row.eventType,
                  payload: row.payload,
                  timestamp: row.timestamp,
                })),
              })
            },
          })
        : { count: 0, isContiguous: true }

    if (
      expectedBaselineReplayCount > 0 &&
      (
        baselineReadResult.isContiguous === false ||
        baselineReadResult.count !== expectedBaselineReplayCount
      )
    ) {
      const latestMeta = await readThreadReplicaMeta(args)
      if (attempt === 0 && didThreadReplicaMetaChangeDuringHydration({ initialMeta: meta, latestMeta })) {
        initialMeta = latestMeta
        continue
      }
      return {
        status: "reset_required",
        reason: "integrity_gap",
        meta,
        targetCursor,
      }
    }

    const expectedRuntimeReplayCount =
      targetCursor >= runtimeStart
        ? targetCursor - runtimeStart + 1
        : 0
    let runtimeReplayCount = 0
    const runtimeReadResult =
      expectedRuntimeReplayCount > 0
        ? await readThreadReplicaLaneEventBatches({
            scopeKey: args.scopeKey,
            sessionId: args.sessionId,
            lane: "runtime",
            fromSequence: runtimeStart,
            toSequence: targetCursor,
            onBatch: (rows) => {
              runtimeReplayCount += rows.length
              // The shared runtime lane holds rows for every conversation on
              // this session. Apply only the rows for the conversation we are
              // hydrating so the projection matches the selected chat tab;
              // contiguity checks still run over the unfiltered rows.
              const matchingRows = rows.filter(
                (row) =>
                  row.lane === "runtime" &&
                  normalizeHydrateConversationId(row.conversationId) === activeConversationId,
              )
              if (matchingRows.length === 0) {
                return
              }
              runtimeState = applyThreadSyncCatchupEventsToManagedRuntimeState({
                currentState: runtimeState,
                events: matchingRows.map((row) => ({
                  id: row.sequence,
                  type: row.eventType,
                  payload: row.payload,
                  timestamp: row.timestamp,
                  childSessionId: row.lane === "runtime" ? row.childSessionId ?? undefined : undefined,
                })),
              })
            },
          })
        : { count: 0, isContiguous: true }

    if (
      expectedRuntimeReplayCount > 0 &&
      (
        runtimeReadResult.isContiguous === false ||
        runtimeReadResult.count !== expectedRuntimeReplayCount
      )
    ) {
      const latestMeta = await readThreadReplicaMeta(args)
      if (attempt === 0 && didThreadReplicaMetaChangeDuringHydration({ initialMeta: meta, latestMeta })) {
        initialMeta = latestMeta
        continue
      }
      return {
        status: "reset_required",
        reason: "integrity_gap",
        meta,
        targetCursor,
      }
    }

    if (targetCursor > EMPTY_THREAD_REPLICA_CURSOR) {
      runtimeState = advanceManagedRuntimeStateCursor({
        currentState: runtimeState,
        nextCursor: targetCursor,
      })
    }

    runtimeState = normalizeManagedRuntimeState({ runtimeState })

    return {
      status: "hydrated",
      meta,
      runtimeState,
      targetCursor,
      checkpointCursor: compatibleCheckpoint?.cursor ?? null,
      baselineReplayCount,
      runtimeReplayCount,
    }
  }

  return { status: "missing", reason: "missing_meta" }
}
